import { observable, action, reaction } from 'mobx';
import { forceCartObserversRefresh, reSyncCart, removeGWPitemsFromCart } from 'src/scripts/utils/cart';
import { getUsersPreferredCurrency, CurrencyState, changeCurrencyCode } from 'src/scripts/utils/currency';
import {
  getUsersPreferredCountry,
  ShippingCountryState,
  changeCountry,
  setShopifyMarketLocale,
} from 'src/scripts/utils/shipping';
import { conditionallyRedirectToINTLwebsite } from '../../../../utils/location';
import { LanguageState } from 'src/scripts/utils/country-language';
import { updateCartBuyerIdentity, fetchCart } from 'src/scripts/utils/cart/cart-apis';
import { transformCartApiResponse } from 'src/scripts/utils/cart/transform-cart-api-response';

export const OPEN_SHIPPING_CURRENCY_MODAL = 'open-shipping-currency-modal';
export const ShippingCurrencyModalState = observable({
  isOpen: false,
});

export const closeShippingCurrencyModalWithoutChanges = action(async () => {
  // Satisfies the following scenario: User opens the Country Picker modal, changes the country, clicks on "Cancel" button instead of "SAVE"; The Country and currency are reverted back to and modal is closed w/out the reload.
  const countryCodeBeforeChange = window.localStorage.getItem('current-country');
  const currencyCodeBeforeChange = window.localStorage.getItem('current-currency');
  countryCodeBeforeChange &&
    changeCountry(countryCodeBeforeChange, {
      updateUserPreference: true,
    });
  currencyCodeBeforeChange && changeCurrencyCode(currencyCodeBeforeChange, { updateUserPreference: true });
  ShippingCurrencyModalState.isOpen = false;
  window.localStorage.removeItem('current-country');
  window.localStorage.removeItem('current-currency');
});

export const openShippingCurrencyModal = action(() => {
  ShippingCurrencyModalState.isOpen = true;
  const currentCountryCode = ShippingCountryState.countryCode || getUsersPreferredCountry();
  const currentCurrencyCode = CurrencyState.currencyCode || getUsersPreferredCurrency();
  // Set temp values for "closeShippingCurrencyModalWithoutChanges" fn.
  currentCountryCode && window.localStorage.setItem('current-country', currentCountryCode);
  currentCurrencyCode && window.localStorage.setItem('current-currency', currentCurrencyCode);
});

export const closeShippingCurrencyModal = action(async () => {
  // Remove temp values
  window.localStorage.removeItem('current-country');
  window.localStorage.removeItem('current-currency');

  window.dataLayer.push({
    event: 'language_shipto_submit',
    location: 'footer', // leave static, adding it in case that module will appear in other places on the site
    shipping_country: ShippingCountryState.countryCode, // Shipping country based on user choice
    language: LanguageState.languageCode, // Language based on user choice
  });

  ShippingCurrencyModalState.isOpen = false;

  if (ShippingCountryState.countryCode && conditionallyRedirectToINTLwebsite(ShippingCountryState.countryCode)) {
    return; // Exit the fn to make sure that Global-E locale change don't fire
  }

  await removeGWPitemsFromCart(ShippingCountryState.countryCode); // Remove ineligible GWP items from the cart on country change
  const customer = window.getCustomerInfo && window.getCustomerInfo();
  const email = customer?.email;

  await updateCartBuyerIdentity({ countryCode: ShippingCountryState.countryCode, email }); // Update buyer identity

  if (ShippingCountryState.countryCode && ShippingCountryState.country?.locale) {
    setShopifyMarketLocale(ShippingCountryState.countryCode, LanguageState.languageCode);
  }

  if (!getUsersPreferredCountry() && ShippingCountryState.countryCode)
    changeCountry(ShippingCountryState.countryCode, {
      updateUserPreference: true,
    });

  if (!getUsersPreferredCurrency() && CurrencyState.currencyCode)
    changeCurrencyCode(CurrencyState.currencyCode, { updateUserPreference: true });
});

document.addEventListener(OPEN_SHIPPING_CURRENCY_MODAL, openShippingCurrencyModal);
export const dispatchOpenShippingModalEvent = () => document.dispatchEvent(new Event(OPEN_SHIPPING_CURRENCY_MODAL));

// eslint-disable-next-line
const cartSideEffectsOnCountryChange = reaction(
  // [REVISIT] Use with loaylty???
  () => ShippingCountryState.countryCode === 'US',
  async (isInUS) => {
    const fetchCartResponse = await fetchCart();
    const warnings = fetchCartResponse && 'warnings' in fetchCartResponse ? fetchCartResponse?.warnings : [];
    await reSyncCart(transformCartApiResponse(fetchCartResponse?.cart, { warnings }), {
      forceUpdateSpecialItems: true,
    });
    forceCartObserversRefresh();
  }
);
